import React, { useState, useRef, useEffect } from 'react';
import { Send, Smile, Sticker, Gift } from 'lucide-react';

interface Message {
  id: string;
  user: {
    name: string;
    avatar: string;
  };
  content: string;
  type: 'text' | 'sticker';
  timestamp: Date;
}

interface Sticker {
  id: string;
  url: string;
  category: string;
}

const stickers: Sticker[] = [
  {
    id: 'workout-1',
    url: 'https://media.giphy.com/media/3oKIPavRPgJYaNI97W/giphy.gif',
    category: 'workout'
  },
  {
    id: 'motivation-1',
    url: 'https://media.giphy.com/media/xT9IgEYXCNqPZnqMuY/giphy.gif',
    category: 'motivation'
  },
  {
    id: 'celebration-1',
    url: 'https://media.giphy.com/media/l3q2Z6S6r0yuiyf5S/giphy.gif',
    category: 'celebration'
  }
];

const demoMessages: Message[] = [
  {
    id: '1',
    user: {
      name: 'Aryan Sharma',
      avatar: 'https://images.unsplash.com/photo-1600486913747-55e5470d6f40?w=50&q=80'
    },
    content: 'Great workout session today! 💪',
    type: 'text',
    timestamp: new Date(Date.now() - 1000 * 60 * 5)
  },
  {
    id: '2',
    user: {
      name: 'Zara Patel',
      avatar: 'https://images.unsplash.com/photo-1611695434398-4f4b330623e6?w=50&q=80'
    },
    content: stickers[0].url,
    type: 'sticker',
    timestamp: new Date(Date.now() - 1000 * 60 * 4)
  },
  {
    id: '3',
    user: {
      name: 'Kabir Singh',
      avatar: 'https://images.unsplash.com/photo-1600878459138-e1123b37cb30?w=50&q=80'
    },
    content: "Who's joining tomorrow's challenge? 🏃‍♂️",
    type: 'text',
    timestamp: new Date(Date.now() - 1000 * 60 * 3)
  }
];

export function ChatRoom() {
  const [messages, setMessages] = useState<Message[]>(demoMessages);
  const [newMessage, setNewMessage] = useState('');
  const [showStickers, setShowStickers] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const [isTyping, setIsTyping] = useState(false);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = (content: string, type: 'text' | 'sticker' = 'text') => {
    if (!content.trim() && type === 'text') return;

    const newMsg: Message = {
      id: Date.now().toString(),
      user: {
        name: 'You',
        avatar: 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=50&q=80'
      },
      content,
      type,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, newMsg]);
    setNewMessage('');
    setShowStickers(false);

    // Simulate response
    if (type === 'text') {
      setIsTyping(true);
      setTimeout(() => {
        const response: Message = {
          id: (Date.now() + 1).toString(),
          user: {
            name: 'Aryan Sharma',
            avatar: 'https://images.unsplash.com/photo-1600486913747-55e5470d6f40?w=50&q=80'
          },
          content: stickers[Math.floor(Math.random() * stickers.length)].url,
          type: 'sticker',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, response]);
        setIsTyping(false);
      }, 2000);
    }
  };

  return (
    <div className="theme-card h-[600px] flex flex-col relative overflow-hidden">
      {/* Chat Header */}
      <div className="p-4 border-b border-border bg-muted/30 backdrop-blur-sm">
        <h3 className="text-lg font-semibold">Club Chat</h3>
        <div className="text-sm text-muted-foreground">28 members online</div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 theme-scrollbar">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex items-start gap-3 ${
              message.user.name === 'You' ? 'flex-row-reverse' : ''
            }`}
          >
            <img
              src={message.user.avatar}
              alt={message.user.name}
              className="w-8 h-8 rounded-full object-cover"
            />
            <div className={`max-w-[70%] ${message.user.name === 'You' ? 'items-end' : ''}`}>
              <div className="text-sm text-muted-foreground mb-1">{message.user.name}</div>
              {message.type === 'text' ? (
                <div className={`rounded-lg p-3 ${
                  message.user.name === 'You'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted'
                }`}>
                  {message.content}
                </div>
              ) : (
                <img
                  src={message.content}
                  alt="Sticker"
                  className="max-w-[200px] rounded-lg"
                />
              )}
            </div>
          </div>
        ))}
        {isTyping && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <div className="w-2 h-2 rounded-full bg-primary animate-bounce" />
            <div className="w-2 h-2 rounded-full bg-primary animate-bounce [animation-delay:0.2s]" />
            <div className="w-2 h-2 rounded-full bg-primary animate-bounce [animation-delay:0.4s]" />
          </div>
        )}
        <div ref={chatEndRef} />
      </div>

      {/* Sticker Picker */}
      {showStickers && (
        <div className="absolute bottom-16 left-0 right-0 bg-background border-t border-border p-4">
          <div className="grid grid-cols-3 gap-4">
            {stickers.map((sticker) => (
              <button
                key={sticker.id}
                onClick={() => handleSendMessage(sticker.url, 'sticker')}
                className="p-2 rounded-lg hover:bg-muted transition-colors"
              >
                <img
                  src={sticker.url}
                  alt="Sticker"
                  className="w-full rounded-lg"
                />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input Area */}
      <div className="p-4 border-t border-border bg-muted/30 backdrop-blur-sm">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowStickers(!showStickers)}
            className="p-2 rounded-lg hover:bg-muted transition-colors"
          >
            <Sticker className="w-5 h-5 text-primary" />
          </button>
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSendMessage(newMessage)}
            placeholder="Type a message..."
            className="flex-1 bg-transparent border-none focus:outline-none"
          />
          <button
            onClick={() => handleSendMessage(newMessage)}
            disabled={!newMessage.trim()}
            className="p-2 rounded-lg bg-primary text-primary-foreground disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}